import {
    ApiBaseService,
    type ApiServiceConfig,
} from "~/services/ApiBaseService";
import type { ApiResponse } from "~/types/ApiResponse";
import type { MessageChannel } from "~/models/Chat";
import { faArrowUpRightFromSquare, faFile } from "@fortawesome/pro-regular-svg-icons";
import { faMoneyBill, faSync } from "@fortawesome/pro-light-svg-icons";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import type { Connection } from "~/models/Connection";

export interface WhatsappMessageTemplateComponent {
    type: string;
    format: string;
    text: string;
    example?: Record<string, string[]>;
    buttons?: {
        type: string;
        text: string;
        url: string;
    }[];
}

export interface WhatsappMessageTemplateItem {
    id: string;
    language: string;
    status: string;
    category: string;
    library_template_name: string;
    components: WhatsappMessageTemplateComponent[];
}

export interface WhatsappMessageTemplate {
    name: string;
    languages: string[];
    status: string;
    category: string;
    library_template_name: string;
    components: WhatsappMessageTemplateComponent[];
    is_template?: boolean;
    items: WhatsappMessageTemplateItem[];
}

export interface WhatsappPhoneNumber {
    verified_name: string;
    code_verification_status: string;
    display_phone_number: string;
    quality_rating: string;
    platform_type: string;
    throughput: Record<string, string>;
    last_onboarded_time: string;
    id: string;
}

export interface WhatsAppConnectionInfo {
    logo: string;
    icon: string;
    label: string;
    connection: any;
}

export default class WhatsappService extends ApiBaseService {
    protected uuid: string;

    constructor(uuid: string, config: ApiServiceConfig) {
        super(config);

        this.uuid = uuid;
    }

    public getMessageTemplates(): Promise<
        ApiResponse<WhatsappMessageTemplate[]>
    > {
        return this.request(
            this.route(
                "api.connections.whatsapp.message_templates.index",
                "/connections/whatsapp/{connection}/message_templates",
                {
                    connection: this.uuid,
                },
            ),
            {
                method: "GET",
            },
        );
    }

    public getMessageTemplate(
        id: string,
    ): Promise<ApiResponse<WhatsappMessageTemplate>> {
        return this.request(
            this.route(
                "api.connections.whatsapp.message_templates.show",
                "/connections/whatsapp/{connection}/message_templates/{id}",
                {
                    connection: this.uuid,
                    id,
                },
            ),
            {
                method: "GET",
            },
        );
    }

    public getPhoneNumbers(): Promise<ApiResponse<MessageChannel[]>> {
        return this.request(
            this.route(
                "api.connections.whatsapp.phone_numbers.index",
                "/connections/whatsapp/{connection}/phone_numbers",
                {
                    connection: this.uuid,
                },
            ),
            {
                method: "GET",
            },
        );
    }

    public getPhoneNumber(id: number): Promise<ApiResponse<MessageChannel>> {
        return this.request(
            this.route(
                "api.connections.whatsapp.phone_numbers.show",
                "/connections/whatsapp/{connection}/phone_numbers/{id}",
                {
                    connection: this.uuid,
                    id,
                },
            ),
            {
                method: "GET",
            },
        );
    }

    public syncPhoneNumber(id: number): Promise<ApiResponse<MessageChannel>> {
        return this.request(
            this.route(
                "api.connections.whatsapp.phone_numbers.sync",
                "/connections/whatsapp/{connection}/phone_numbers/{id}/sync",
                {
                    connection: this.uuid,
                    id,
                },
            ),
            {
                method: "POST",
            },
        );
    }

    public updatePhoneNumberProfile(
        id: number,
        data: any,
    ): Promise<ApiResponse<MessageChannel>> {
        // We do not process the URL version, only files
        if (
            data &&
            data.profile_picture_url &&
            typeof data.profile_picture_url === "string"
        ) {
            delete data.profile_picture_url;
        }

        return this.request(
            this.route(
                "api.connections.whatsapp.phone_numbers.save",
                "/connections/whatsapp/{connection}/phone_numbers/{id}",
                {
                    connection: this.uuid,
                    id,
                },
            ),
            {
                method: "POST",
                body: jsonToFormData(data),
                query: {
                    _method: "PATCH",
                },
            },
        );
    }

    copyMessageTemplate(id: string): Promise<ApiResponse<MessageChannel>> {
        return this.request(
            this.route(
                "api.connections.whatsapp.message_templates.copy",
                "/connections/whatsapp/{connection}/message_templates/{id}/copy",
                {
                    connection: this.uuid,
                    id,
                },
            ),
            {
                method: "POST",
            },
        );
    }

    listConnections(): Promise<
        ApiResponse<Connection[], WhatsAppConnectionInfo>
    > {
        return this.request(
            this.route("whatsapp.connections.index", "/connections/whatsapp"),
        );
    }

    findConnection(): Promise<ApiResponse<Connection>> {
        return this.request(
            this.route(
                "whatsapp.connections.show",
                "/connections/whatsapp/{connection}",
                {
                    connection: this.uuid,
                },
            ),
        );
    }
    listConnectionChannels(): Promise<ApiResponse<MessageChannel[]>> {
        return this.request(
            this.route(
                "whatsapp.connections.channels.list",
                "/connections/whatsapp/{connection}/channels",
                {
                    connection: this.uuid,
                },
            ),
        );
    }
    findConnectionChannel(id: string): Promise<ApiResponse<MessageChannel>> {
        return this.request(
            this.route(
                "whatsapp.connections.channels.show",
                "/connections/whatsapp/{connection}/channels/{channel}",
                {
                    connection: this.uuid,
                    channel: id,
                },
            ),
        );
    }

    public getHealthMessagesForChannel(object: MessageChannel) {
        const health_messages = [];

        for (const entity of object.metadata.whatsapp?.health_status
            ?.entities ?? []) {
            if (entity.additional_info) {
                health_messages.push(
                    ...entity.additional_info.map((info) => ({
                        title: info,
                    })),
                );
            }

            if (entity.errors) {
                health_messages.push(
                    ...entity.errors.map((error) => ({
                        title: error.error_description,
                        description: error.possible_solution,
                    })),
                );
            }
        }

        return health_messages;
    }

    public getTierBadgeForChannel(object: MessageChannel) {
        const tier_messages = [
            {
                title: "Messages you can send with this number on a rolling 24h basis.",
            },
        ];

        switch (object.metadata?.whatsapp?.messaging_limit_tier) {
            case "TIER_50":
                return {
                    label: "Tier: 50",
                    messages: tier_messages,
                };
            case "TIER_250":
                return {
                    label: "Tier: 250",
                    messages: tier_messages,
                };
            case "TIER_1K":
                return {
                    label: "Tier: 1K",
                    messages: tier_messages,
                };
            case "TIER_10K":
                return {
                    label: "Tier: 10K",
                    messages: tier_messages,
                };
            case "TIER_100K":
                return {
                    label: "Tier: 100K",
                    messages: tier_messages,
                };
            case "TIER_UNLIMITED":
                return {
                    label: "Tier: Unlimited",
                    messages: tier_messages,
                };
        }
    }

    public getQualityBadgeForChannel(object: MessageChannel) {
        const quality_messages = [
            {
                title: "Quality score of this phone number.",
            },
        ];

        switch (object.metadata.whatsapp?.quality_score) {
            case "GREEN":
                return {
                    label: "High Quality",
                    color: "green",
                    messages: quality_messages,
                };
            case "YELLOW":
                return {
                    label: "Medium Quality",
                    color: "yellow",
                    messages: quality_messages,
                };
            case "RED":
                return {
                    label: "Low Quality",
                    color: "red",
                    messages: quality_messages,
                };
            default:
                return {
                    label: "Quality Pending",
                    color: "hd-neutral",
                    messages: quality_messages,
                };
        }
    }

    public getBadgesForChannel(object: MessageChannel) {
        return [
            this.getStatusBadgeForChannel(object),
            this.getQualityBadgeForChannel(object),
            this.getTierBadgeForChannel(object),
            this.getHealthBadgeForChannel(object),
        ].filter((badge) => !!badge);
    }

    public getHealthBadgeForChannel(object: any) {
        const health_messages = this.getHealthMessagesForChannel(object);

        switch (object.metadata.whatsapp?.health_status.can_send_message) {
            case "BLOCKED":
                return {
                    label: "Blocked",
                    color: "red",
                    list_type: "ordered",
                    messages: health_messages,
                };
            case "LIMITED":
                return {
                    label: "Limited",
                    color: "orange",
                    list_type: "ordered",
                    messages: health_messages,
                };
            case "AVAILABLE":
                return {
                    label: "Available",
                    color: "green",
                    list_type: "ordered",
                    messages: health_messages,
                };
        }
    }

    public getStatusBadgeForChannel(object: any) {
        const colors = {
            active: "green",
            pending: "hd-neutral",
            deleted: "red",
            migrated: "hd-neutral",
            banned: "red",
            restricted: "yellow",
            rate_limited: "yellow",
            flagged: "red",
            connected: "green",
            disconnected: "red",
            unknown: "hd-neutral",
            unverified: "yellow",
        };
        return {
            label: object.status.label,
            color: colors[object.status.value as keyof colors],
            messages: [
                {
                    title: 'The current status of this phone number. If it is "Connected" you\'re good to go!',
                },
            ],
        };
    }

    public getConnectionDropdown(
        connection: Connection,
        options: Partial<{
            onProvision: (e: PointerEvent) => void;
            onDelete: (e: PointerEvent) => void;
        }> = {},
    ) {
        const payment_settings_url = `https://business.facebook.com/billing_hub/payment_methods?asset_id=${connection.external_webhook_id}`;
        const whatsapp_templates_url = `https://business.facebook.com/latest/whatsapp_manager/message_templates?tab=message-templates&asset_id=513348301856079${connection.external_webhook_id}`;

        return [
            [
                {
                    label: `WhatsApp Manager`,
                    icon: faArrowUpRightFromSquare,
                    click: () => {
                        navigateTo(
                            "https://business.facebook.com/wa/manage/home/?waba_id=" +
                                connection.external_webhook_id,
                            {
                                open: {
                                    target: "_blank",
                                },
                                external: true,
                            },
                        );
                    },
                },
                {
                    label: `Manage Templates`,
                    icon: faFile,
                    click: () => {
                        navigateTo(whatsapp_templates_url, {
                            open: {
                                target: "_blank",
                            },
                            external: true,
                        });
                    },
                },
                {
                    label: `Payment Methods`,
                    icon: faMoneyBill,
                    click: () => {
                        navigateTo(payment_settings_url, {
                            open: {
                                target: "_blank",
                            },
                            external: true,
                        });
                    },
                },
                {
                    label: `Sync Account`,
                    icon: faSync,
                    click: (e: PointerEvent) => {
                        if (options.onProvision) options.onProvision(e);
                        useApi().connections.provision(connection.uuid);
                    },
                },
                {
                    label: `Disconnect`,
                    icon: faTrash,
                    click: (e: PointerEvent) => {
                        if (options.onDelete) options.onDelete(e);
                    },
                },
            ],
        ];
    }

    public getChannelDropdown(
        connection: Connection,
        channel: MessageChannel,
        options: Partial<{
            withGenericLinks?: boolean;
            onSync: (e: PointerEvent) => void;
        }> = {},
    ) {
        const payment_settings_url = `https://business.facebook.com/billing_hub/payment_methods?asset_id=${connection.external_webhook_id}`;
        const whatsapp_templates_url = `https://business.facebook.com/latest/whatsapp_manager/message_templates?tab=message-templates&asset_id=513348301856079${connection.external_webhook_id}`;
        const genericLinks = options.withGenericLinks ? [
            {
                label: `Manage Templates`,
                icon: faFile,
                click: () => {
                    navigateTo(whatsapp_templates_url, {
                        open: {
                            target: "_blank",
                        },
                        external: true,
                    });
                },
            },
            {
                label: `Payment Methods`,
                icon: faMoneyBill,
                click: () => {
                    navigateTo(payment_settings_url, {
                        open: {
                            target: "_blank",
                        },
                        external: true,
                    });
                },
            }
        ] : [];
        const phone = (
            channel?.metadata?.whatsapp?.display_phone_number ?? ""
        ).replace(/\D/g, "");
        return [
            [
                {
                    label: `WhatsApp Manager`,
                    icon: faArrowUpRightFromSquare,
                    click: () => {
                        navigateTo(
                            `https://business.facebook.com/wa/manage/phone-numbers/?waba_id=${connection.external_webhook_id}&phone_number=${phone}`,
                            {
                                open: {
                                    target: "_blank",
                                },
                                external: true,
                            },
                        );
                    },
                },
                ...genericLinks,
                {
                    label: `Sync Phone Number`,
                    icon: faSync,
                    click: (e: PointerEvent) => {
                        if (options.onSync) options.onSync(e);
                    },
                },
            ],
        ];
    }
}
